import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineLite, TweenLite, Power0} from "gsap";
import {TheCrypt} from "../TheCrypt";
import {NolimitConfig} from "@nolimitcity/slot-launcher/bin/settings/NolimitConfig";
import {SpriteCollector} from "./collector/SpriteCollector";
import {MSymbolCollector} from "./collector/MSymbolCollector";
import {M1Collector} from "./collector/M1Collector";
import {TheCryptAvalancheReelModel} from "../reels/TheCryptAvalancheReelModel";
import {ISymbolData, TheCryptSymbol} from "../symbols/TheCryptSymbol";
import {AnimatedMeshRope} from "../components/AnimatedMeshRope";
import {TheCryptGameConfig} from "../TheCryptGameConfig";
import {TheCryptHelper} from "../utils/TheCryptHelper";
import {TheCryptSoundManager} from "../sound/TheCryptSoundManager";
import {TheCryptSoundConfig} from "../configs/TheCryptSoundConfig";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";

export class TheCryptCollector extends PIXI.Container {
    private _collectors: MSymbolCollector[];
    private _collectorM1: M1Collector;
    private _locatePositionSprite: PIXI.Sprite;
    private _allSwooshes: AnimatedMeshRope[];
    private _dragonSideReel: [number, number] = [0, 0];

    constructor() {
        super();
        this.name = "Collector";
        this._collectors = [];
        this._allSwooshes = [];
        this.init();
    }

    private init() {
        const meterPlate: PIXI.Sprite = new PIXI.Sprite(TheCrypt.assetLoader.getTextures("meter/meter_plate")[0]);
        meterPlate.anchor.set(0.5, 0);
        meterPlate.position.set(0, -26);
        this.addChild(meterPlate);
        this._collectors = ArrayHelper.initArrayWithValues(5, (i: number) => new MSymbolCollector(6 - i, this));
        this._collectorM1 = new M1Collector(1, this);
        this._collectorM1.position.set(895, 97);
        this.visible = false;
        this._locatePositionSprite = new PIXI.Sprite();
    }

    public collect(): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            data: [number, number] = TheCrypt.model.data.dragonSideReel;

        if (data[1] > this._dragonSideReel[1]) {
            this._dragonSideReel[1] = data[1];
            tl.add(this.collectAnimationM1());
        }

        if (data[0] > this._dragonSideReel[0]) {
            this._dragonSideReel[0] = data[0];
            tl.add(this.collectAnimation());
        }

        return tl;
    }

    protected patchWildOrMultiplier(isM1: boolean, mId: number = -1): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const M1Multiplier: number = TheCrypt.model.data.m1NextMultiplier,
            toPatchSym: string = `M${isM1 ? 1 : mId}`;
        let id: number = -1;
        TheCrypt.realArea.allReels.forEach((reel: TheCryptAvalancheReelModel, i: number) => {
            reel.reelView.getAllSymbols().forEach((sym: TheCryptSymbol, j: number) => {
                if (sym.symName === toPatchSym) {
                    id++;
                    tl.add([
                        ()=>TheCryptSoundManager.playSound(TheCryptSoundConfig.SYM_TO_WILD),
                        TheCrypt.realArea.getSymbol([i, j]).patchMidSymWildOrMultiplier(isM1 ? M1Multiplier : -1)
                    ], id * 0.5);
                }
            });
        });
        return tl;
    }

    private getCollectorPositions(): number[] {
        const pos: number[] = [];
        let startPos: number = 30;

        this.collectors.forEach((collector: SpriteCollector, i: number) => {
            startPos += collector.currentHeight * 0.5;
            Logger.logDev(`${startPos} collector is active: ${collector.isActive}, at step: ${collector.progress}`)
            pos.push(startPos);
            startPos += collector.currentHeight * 0.5;
        });
        return pos;
    }

    private updateCollectorPositions(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const Ys: number [] = this.getCollectorPositions();

        this._collectors.forEach((collector: SpriteCollector, i: number) => {
            tl.add(TweenLite.to(collector, 3 * 0.03, {y: Ys[i], ease: Power0.easeOut}), 0);
        });
        return tl;
    }

    protected checkServer(collector: SpriteCollector, i: number): void {
        if (NolimitConfig.isDevMode) {
            if ((collector.progress) != (TheCrypt.model.data.dragonSideReel[0] - i * 4) % 4) {
                debugger;
            }
        }
    }

    private getPathData(toX: number, toY: number, fromX?: number, fromY?: number): PIXI.IPointData[] {
        if (!fromX) {
            fromX = 0;
        }
        if (!fromY) {
            fromY = 0;
        }
        return [{x: fromX, y: fromY}, {x: toX, y: fromY}, {x: toX, y: toY * 0.8}, {x: toX, y: toY}];
    }

    private playSwoosh(isM1: boolean, container: PIXI.Container, pointsData: PIXI.IPointData[], playSound: boolean = false): void {
        const swoosh = new AnimatedMeshRope(TheCrypt.assetLoader.getTextures(`effects/fs_win_swoosh${isM1 ? "_m1" : ""}/fs_win_swoosh_`),
            pointsData);
        swoosh.blendMode = PIXI.BLEND_MODES.SCREEN;
        TheCrypt.initialWinView.addChild(swoosh);
        swoosh.playAnimation();
        //TheCrypt.playSoundEffect(PearlHarborSoundConfig.FS_SWOOSH);
    }

    private updateCollector(collector: SpriteCollector, M1Win: boolean): TimelineLite {
        TheCrypt.initialWinView.addChild(this._locatePositionSprite);
        const tl: TimelineLite = new TimelineLite();
        let delay: number = 0;
        const sounds = ArrayHelper.shuffle(TheCryptSoundConfig.COLLECT_SWOOSH);
        TheCrypt.model.allWinPositionSymbols.forEach((reel: string[], i: number) => {
            const x: number = TheCryptGameConfig.reelPositions[i].x + TheCryptGameConfig.symbolWidth * 0.5;
            delay += (i>2? 0.05:0.08);
            tl.add(() => {
                TheCryptSoundManager.playSound(sounds[i]);
            }, delay);
            reel.forEach((sym: string, j: number) => {
                if (M1Win ? (sym === "M1") : (sym.length > 0 && sym != "M1")) {
                    this._locatePositionSprite.position = TheCryptHelper.copyLocalPosition(this._locatePositionSprite, collector);
                    const symbol: TheCryptSymbol = TheCrypt.realArea.getSymbol([i, j]),
                        path = this.getPathData(this._locatePositionSprite.position.x + (M1Win ? 2 : -1) * 10, this._locatePositionSprite.position.y, x, symbol.position.y);
                    tl.add(() => {
                        this.playSwoosh(M1Win, TheCrypt.initialWinView, path, false);
                        symbol.collect();
                    }, delay);
                }
            });
        });

        tl.add(collector.upgrade());
        return tl;
    }

    public collectAnimation(): TimelineLite {
        const data: number[] = TheCrypt.model.sideBarProgress,
            tl: TimelineLite = new TimelineLite();
        this._collectors.forEach((collector: MSymbolCollector, i: number) => {
            if (data[i] > collector.progress) {
                tl.add(this.updateCollector(collector, false));
            }
        });

        return tl;
    }

    public collectAnimationM1(): TimelineLite {
        const progress: number = TheCrypt.model.sideBarProgress[5],
            tl: TimelineLite = new TimelineLite(),
            collector: M1Collector = this._collectorM1;
        if (progress > collector.progress) {
            tl.add(this.updateCollector(collector, true));
        }
        return tl;
    }

    public show(isRestore: boolean): TimelineLite {
        const Ys: number[] = this.getCollectorPositions();
        const tl: TimelineLite = new TimelineLite();
        this._collectors.forEach((collector: MSymbolCollector, i) => {
            collector.position.set(0, Ys[i]);
            tl.add([
                () => {
                    this.addChild(collector);
                    TheCryptSoundManager.playMeterShowingSound();
                },
                collector.show("meter", isRestore)
            ], "-=0.5");
        });
        tl.add(() => {
            this.visible = true;
            this.onResize();
        }, 0);
        this._collectorM1.alpha = 0;
        this._collectorM1.show("meterM1", isRestore);
        tl.add(TweenLite.to(this._collectorM1, 1, {alpha: 1}));
        if (!isRestore) {
            this._collectors[0].isActive = true;
            tl.add(this._collectors[0].activate());
        } else {
            tl.add(() => {
                this._collectors.forEach((collector: MSymbolCollector) => {
                    collector.updateStatesOnRestore();
                });
            });
        }
        tl.add([
            ()=>TheCryptSoundManager.playSound(TheCryptSoundConfig.METER_SHOW),
            this.updateCollectorPositions()
        ]);
        tl.add(TheCrypt.gameViewContainer.removeDimmer());
        return tl;
    }

    public showFullFilledCollectorAnimation(): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            MSymbolId: number = this.fullyChargedMSymbolCollector;

        if (MSymbolId > -1) {
            tl.add(this._collectors[MSymbolId].fullyCharged(), 0);
            if ((MSymbolId + 1) < this._collectors.length) {
                tl.add(this._collectors[MSymbolId + 1].activate(), 0)
            }
            tl.add(this.updateCollectorPositions(), 0);
            tl.add(TheCrypt.extraFreespinAwardView.show());
            tl.add(this.patchWildOrMultiplier(false, 6 - MSymbolId));
        }

        if (this._collectorM1.isFullyCharged) {
            tl.add(this._collectorM1.fullyCharged());
            tl.add(this.patchWildOrMultiplier(true));
        }

        return tl;
    }

    public hide(): void {
        this.visible = false;
    }

    public reset(): void {
        this._dragonSideReel = [0, 0];
        this._collectors.forEach((collector: SpriteCollector) => collector.reset());
    }

    public get collectors(): SpriteCollector[] {
        return this._collectors;
    }

    public get fullyChargedM1Collector(): boolean {
        return (<M1Collector>this._collectorM1).isFullyCharged;
    }

    public get fullyChargedMSymbolCollector(): number {
        let fullFilledMeterId: number = -1;
        this._collectors.forEach((collector: MSymbolCollector, i: number) => {
            if (collector.isFullyCharged) {
                fullFilledMeterId = i;
            }
        });

        return fullFilledMeterId;
    }

    public onResize() {
        if (!this.visible) {
            return;
        }
        if (NolimitApplication.isLandscape) {
            this.position.set(TheCryptHelper.isIpad?-62.5:-90, 61);
            this._collectorM1.position.set(TheCryptHelper.isIpad?835:895, 97);
            this.rotation = 0;
            this._collectors.forEach((collector: SpriteCollector, i: number) => {
                collector.rotation = 0;
            });
            this._collectorM1.rotation = 0;
        } else {
            this.position.set(15, TheCryptHelper.isIpad?-25:-100);
            this.rotation = -Math.PI * 0.5;
            this._collectors.forEach((collector: SpriteCollector, i: number) => {
                collector.rotation = Math.PI * 0.5;
            });
            this._collectorM1.rotation = Math.PI * 0.5;
            this._collectorM1.position.set(0, 619);
        }
    }
}